import React, { memo, useEffect, useRef, useState } from 'react';
import { Button, Stack, Typography } from '@mui/material';
import Add from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';

import { CardWrapper, useHookForm } from '@monorepo/common';

import { ArrowRight as ArrowRightIcon } from '../../../../components/Icons/arrow-right';
import { IEventsStepProps } from '../../interfaces';
import { itemLocation } from '../staticValues';
import LocationTitle from '../../../../components/JobStepper/components/LocationTitle';
import { IStep2Form } from './formInterfaces';
import Location from './components/Location';
import Salary from './components/Salary';
import { schema } from './schema';
import { useCommitOnLeave } from '../useCommitOnLeave';

interface IStep3Props extends IEventsStepProps {
  defaultData: IStep2Form;
  setStep2: (val: IStep2Form) => void;
  onSave?: (val: IStep2Form) => void;
  onDraftChange?: (val: IStep2Form) => void;
  registerCommit?: (commitNow: () => void) => () => void;
}

export const Step2 = memo(({
  onNext,
  onBack,
  onSave,
  Nav,
  defaultData,
  setStep2,
  onDraftChange,
  registerCommit,
}:IStep3Props) => {
  const submitIntent = useRef<'next' | 'save'>('next');
  const [isShowLocation, setIsShowLocation] = useState(!!defaultData.locations.length);
  const [changedFields, setChangedFields] = useState<IStep2Form | null>(null);

  const { register, errors, getValues, setValue, control, handleSubmit, useFieldArray, setFormData } = useHookForm<IStep2Form>({
    schema,
    showChangedFields: (a) => {
      setChangedFields(a);
      onDraftChange?.(a);
    }
  });
  const markCommitted = useCommitOnLeave(() => {
    const values = getValues();
    if (JSON.stringify(values) !== JSON.stringify(defaultData)) setStep2(values);
  }, registerCommit);
  const goBack = () => {
    if (onBack) {
      if (changedFields && (JSON.stringify(defaultData).toString() !== JSON.stringify(changedFields))) {
        setStep2(changedFields);
      }
      markCommitted();
      onBack();
    }
  };

  const { fields, prepend, replace, remove } = useFieldArray({
    name: 'locations',
    control
  });

  const increase = () => {
    prepend(itemLocation);
  };

  const decrease = (id: number) => {
    if (fields.length === 1) {
      setValue('locations', [itemLocation]);
      return;
    }
    remove(id);
  };
  const onSubmit = (data: IStep2Form) => {
    if (data) {
      // The "at least one location" rule now lives in the schema (locations.min(1)
      // when not remote), so a failure is blocked by handleSubmit and shown inline
      // below LocationTitle — matching the Post a Job flow instead of a toast.
      setStep2(data);
      if (submitIntent.current === 'save') {
        onSave?.(data);
      } else {
        markCommitted();
        onNext?.();
      }
      submitIntent.current = 'next';
    }
  };

  const toggleLocation = (status: boolean) => {
    setIsShowLocation(!status);
    if (!isShowLocation) {
      increase();
    }
  };

  useEffect(() => {
    // JobInfo never sends `noneLocation`, so it always arrives false/undefined here.
    // Treat a job with no saved locations as remote; a job with locations is in-person.
    setFormData(defaultData);
    replace(defaultData.locations);
  }, []);

  useEffect(() => {
    setValue('noneLocation', !isShowLocation);
    if (!isShowLocation) {
      setValue('locations', []);
    }
  }, [isShowLocation]);

  return (
    <CardWrapper
      scrollOptions={{
        isErrorsAutoScrollPage: errors
      }}
    >
      <form
        onSubmit={handleSubmit(onSubmit)}
        data-cy="step2"
      >
        <Salary
          defaultValueMax={defaultData.salaryMax}
          defaultValueMin={defaultData.salaryMin}
          defaultValueExact={defaultData.salaryExact}
          defaultValueSalaryUnitId={defaultData.salaryUnitId}
          defaultValueRadio={defaultData.salaryUnitRadio}
          errors={errors}
          setValue={setValue}
          getValues={getValues}
          register={register}
        />

        <LocationTitle
          value={!isShowLocation}
          toggleLocation={toggleLocation}
        />

        {/* Array-level message from the schema's locations.min(1) rule. The rows
            below only render errors.locations[index], so this surfaces the
            "add at least one location" error inline instead of as a toast. */}
        {isShowLocation && errors?.locations?.message && (
          <Typography
            variant="body2"
            color="error"
            sx={{ mb: 2 }}
          >
            {errors.locations.message}
          </Typography>
        )}

        {isShowLocation && (
        <>
          {fields.map((item, index) => (
            <Location
              key={item.id}
              register={register}
              fieldLength={fields.length || 0}
              pIsShowState={`locations.${index}.isShowState` as const}
              pAddress1={`locations.${index}.address1` as const}
              pAddress2={`locations.${index}.address2` as const}
              pCity={`locations.${index}.city` as const}
              pState={`locations.${index}.state` as const}
              pZip={`locations.${index}.zip` as const}
              pProvince={`locations.${index}.province` as const}
              pCountry={`locations.${index}.country` as const}
              decrease={decrease}
              id={index}
              setValue={setValue}
              errors={{
                ...(errors?.locations) && ({ ...errors.locations[index] })
              }}
              getValues={getValues}
            />
          ))}
        </>
        )}

        {isShowLocation && (
        <Button
          sx={{ mt: 2 }}
          type="button"
          variant="outlined"
          color="info"
          startIcon={<Add />}
          onClick={() => {
            increase();
          }}
        >
          Add an additional location
        </Button>
        )}
        <Nav
          nextDisabled={false}
          onBack={goBack}
          nextButton={
              (
                <Stack
                  className="cpg-edit-step-actions"
                  direction={{ xs: 'column', sm: 'row' }}
                >
                  <Button
                    startIcon={<SaveIcon />}
                    variant="outlined"
                    type="submit"
                    onClick={() => {
                      submitIntent.current = 'save';
                    }}
                    data-cy="saveListing"
                  >
                    Save Listing
                  </Button>
                  <Button
                    endIcon={(<ArrowRightIcon fontSize="small" />)}
                    variant="contained"
                    data-cy="nextStep"
                    disabled={false}
                    type="submit"
                    onClick={() => {
                      submitIntent.current = 'next';
                    }}
                  >
                    Next Step
                  </Button>
                </Stack>
              )
            }
        />
      </form>
    </CardWrapper>
  );
});
